<template>
    <div class="flex flex-col gap-2 grow h-[calc(100%-40px)]">
        <div class="flex gap-4">
            <StatusTable />
            <SubAccStatus v-if="isSubAccount" :is-customer="isCustomer"/>
            <AccountStatus v-else/>
        </div>
        <div class="flex flex-col grow gap-2 border border-default rounded-lg bg-primary-content" @drop.prevent="onDrop">
            <UCollapsible
                v-model:open="data.fileOptionsOpen"
                class="flex flex-col gap-2 w-full mb-2"
                :unmount-on-hide="false"
            >
                <UButton
                    class="group text-md text-xs flex items-center w-full mt-1 mx-2"
                    :label="`${data.fileOptionsOpen ? 'Skjul' : 'Vis'} filvalg`"
                    color="white"
                    variant="subtle"
                    :ui="{
                        label: 'flex items-center gap-1 underline underline-offset-3 hover:no-underline',
                    }"
                />
                <template #content>
                    <div class="p-2 pt-0 flex gap-4">
                        <div class="flex flex-col gap-2 w-full">
                            <UFileUpload
                                v-model="file"
                                accept=".csv"
                                label="Dra og slipp eller last opp fil"
                                description=".csv"
                                class="w-full h-36"
                                position="none"
                                @update:model-value="() => file && readFile(file)"
                            />
                            <div v-if="data.fileName" class="flex gap-2 border rounded-lg border-default p-1 justify-between">
                                <div class="flex items-center gap-1 text-sm"><i class="i-ph-file-csv"/>{{ data.fileName }}</div>
                                <UTooltip text="Fjern opplastet fil og nullstill data">
                                    <UButton
                                        color="error"
                                        variant="ghost"
                                        icon="i-ph-x"
                                        size="sm"
                                        @click="() => {
                                            composable.clear()
                                            file = null
                                        }"
                                    />
                                </UTooltip>
                            </div>
                        </div>

                        <csv-column-mapper
                            v-if="data.csvData.length"
                            v-model="data.columnMapping"
                            v-model:firstRowIsHeader="data.firstRowIsHeader"
                            class="w-full"
                            :sample-row="data.csvData[0]"
                            :data-grid-columns="columns"
                            @mapping-changed="redrawRows"
                            @update:first-row-is-header="redrawRows"
                        />
                        <div v-else class="w-full flex justify-center items-center text-base-content/80 text-sm">Kolonnevalg vil vises her</div>
                    </div>
                </template>
            </UCollapsible>

            <div class="grow mx-2">
                <ag-grid-vue
                    class="w-full h-full py-1"
                    :theme="agTheme"
                    :grid-options="gridOptions"
                    :row-data="data.csvData"
                    :column-defs="columns"
                    :default-col-def="{ sortable: true, filter: true }"
                />
            </div>
            <UButton
                label="Legg til linje"
                icon="i-ph-plus"
                size="sm"
                color="neutral"
                variant="outline"
                class="w-fit mx-2 mb-2"
                @click="addNewRow"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {computed, ref} from 'vue'
import Papa from 'papaparse'
import CsvColumnMapper from '@/incoming-balance/CsvColumnMapper.vue'
import {getValue, updateRowValue} from '@/incoming-balance/CsvColumnMapper.service'
import {GetContextMenuItemsParams, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IRowNode, RowClassParams, ValueGetterParams, ValueSetterParams} from 'ag-grid-community'
import {agTheme} from '@/agUtils/ag-theme.service'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import SubAccountActionCell from '@/incoming-balance/SubAccountActionCell.vue'
import BalanceAccountActionCell from '@/incoming-balance/BalanceAccountActionCell.vue'
import {getSubAccountRowActions} from '@/incoming-balance/subAccountActions'
import {getAccountRowActions} from '@/incoming-balance/accountActions'
import SubAccStatus from '@/incoming-balance/SubAccStatus.vue'
import AccountStatus from '@/incoming-balance/AccountStatus.vue'
import {CsvGridComponentApi} from '@/incoming-balance/types/CsvGridComponentApi.type'
import {CsvDataRow} from '@/incoming-balance/types/CsvDataRow.type'
import useTrialBalance from '@/incoming-balance/tribal.composable'
import subAccountComposable from '@/incoming-balance/subaccount.composable'
import StatusTable from '@/incoming-balance/StatusTable.vue'
import TransStatusCell from '@/incoming-balance/TransStatusCell.vue'
import {v4 as uuidv4} from 'uuid'

const {useCustomerBalance, useSupplierBalance} = subAccountComposable()

const file = ref(null)
const gridApi = ref<GridApi>()

const props = defineProps({
    isSubAccount: {
        type: Boolean,
        required: false,
        default: false
    },
    isCustomer: {
        type: Boolean,
        required: false,
        default: false
    }
})

const composable = computed(() => {
    if (props.isSubAccount && props.isCustomer) {
        return useCustomerBalance()
    }
    if (props.isSubAccount && !props.isCustomer) {
        return useSupplierBalance()
    }
    return useTrialBalance()
})

const data = computed<CsvGridComponentApi>(() => composable.value.data.value)

const redrawRows = () => {
    if(props.isSubAccount) reassignSubAccountUuids()
    gridApi.value?.redrawRows()
}

const valueGetter = (params: ValueGetterParams<CsvDataRow>) => {
    if (params.data)
        return getValue(params.colDef.field || '', params.data, data.value.columnMapping)
}

const valueSetter = (params: ValueSetterParams<CsvDataRow>) => {
    const columnKey = params.colDef.field as string
    const newRow = updateRowValue(columnKey, params.newValue, params.data, data.value.columnMapping)

    const needsToReassignSubAccountUuid = props.isSubAccount && (columnKey === 'subAccName' || columnKey === 'subAccIdentification')
    if (needsToReassignSubAccountUuid) reAssignSubAccountUuidForRow(newRow)

    const rowIndex = data.value.csvData.findIndex(row => row[0] === newRow[0])
    data.value.csvData[rowIndex] = newRow

    params?.node?.setData(newRow)

    return true
}

const ignoreFirstRow = (rowNode: IRowNode<CsvDataRow>) => {
    return data.value.firstRowIsHeader && rowNode?.rowIndex === 0
}

const isRowIgnored = (rowNode: IRowNode<CsvDataRow>) => {
    const csvRow: CsvDataRow | undefined = rowNode.data
    if (!csvRow) return true
    const colMapping = data.value.columnMapping
    if (ignoreFirstRow(rowNode)) return true
    if (props.isSubAccount) {
        const amount = Number(getValue('amount', csvRow, colMapping))
        return amount === 0
    } else {
        const balance = Number(getValue('balance', csvRow, colMapping))
        const accountNo = getValue('accountNo', csvRow, colMapping) as string
        const validAccountNo = accountNo.match(/^\d{4}$/)
        return (!validAccountNo || balance === 0 || [CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO].includes(accountNo))
    }
}

const columns = computed(() => {
    const dynamicCols = data.value.columnConfig.map(colDef => ({
            ...colDef,
            editable: true,
            valueGetter,
            valueSetter,
            flex: 1
        })
    )

    return [
        ...dynamicCols,
        {
            headerName: 'Bokføres',
            field: 'transStatus',
            cellClass: 'text-center',
            valueGetter: (params: ValueGetterParams<CsvDataRow>) => {
                if (!params.node) return ''
                if (ignoreFirstRow(params.node)) return
                return isRowIgnored(params.node) ? 'IGNORE' : 'POST'
            },
            cellRendererSelector: () => {
                return {component: TransStatusCell}
            },
            flex: 1
        },
        {
            headerName: 'Feilmelding',
            field: 'error',
            cellClass: 'text-error',
            minWidth: 220,
            flex: 1,
            cellRendererSelector: (params: ICellRendererParams) => {
                if (ignoreFirstRow(params.node)) return
                return {component: props.isSubAccount ? SubAccountActionCell : BalanceAccountActionCell}
            }
        },
    ]
})

const gridOptions: GridOptions = {
    getRowStyle: (rowNode: RowClassParams) => {
        if (ignoreFirstRow(rowNode.node))
            return {opacity: '50%'}
    },
    onGridReady: ({api}: GridReadyEvent) => {
        gridApi.value = api
    },
    getRowId: ({data}: { data: CsvDataRow }) => String(data[0]), // first cell = unique id
    context: {
        isSubAccount: props.isSubAccount,
        isCustomer: props.isCustomer,
        columnMapping: data.value.columnMapping,
        csvData: data.value.csvData,
        firstRowIsHeader: data.value.firstRowIsHeader
    },
    overlayNoRowsTemplate: `<span class="text-sm text-base-content/80">
        ${props.isSubAccount ? (props.isCustomer ? 'Kunder' : 'Leverandører') : 'Kontoer'} vil vises her
    </span>`,
    getContextMenuItems: (params: GetContextMenuItemsParams<CsvDataRow>) => {
        if(!params?.node) return []
        const row = params.node.data
        if (!row) return []
        const rowActions = props.isSubAccount ?
            getSubAccountRowActions(row, props.isCustomer):
            getAccountRowActions(row)
        const deleteAction = {
            name: 'Slett linje',
            icon: '<i class="i-ph-trash inline-block align-middle"/>',
            action: () => {
                if(params.node?.data == null) return
                const idToDelete = params.node.data[0] // first cell = unique id

                data.value.csvData = data.value.csvData.filter(r => r[0] !== idToDelete)
            }
        }

        return [...rowActions, deleteAction]
    },
    suppressCellFocus: true,
    suppressScrollOnNewData: true,
}

// @ts-ignore
const onDrop = (event) => {
    if (event.dataTransfer.items) {
        if (event.dataTransfer.items.length > 1) {
            console.warn('Multiple files dropped, only the first will be processed')
            alert('Sorry, kun én fil om gangen er støttet foreløpig')
            return
        }
        [...event.dataTransfer.items].forEach((item) => {
            if (item.kind === 'file') {
                const file = item.getAsFile()
                readFile(file)
            }
        })
    }
}

const readFile = (file: File) => {
    if (!file) return
    data.value.fileName = file.name
    const reader = new FileReader()

    const parseFile = (fileContent: string) => {
        const parsed = Papa.parse(fileContent, {
            skipEmptyLines: true
        })
        const rowsWithIds = (parsed.data as CsvDataRow[]).map((row) => [
            uuidv4(),
            ...row,
        ])
        composable.value.setCsvData(rowsWithIds)
    }

    reader.onload = (e) => {
        const textUtf8 = e.target?.result as string
        if (!textUtf8) return

        const looksBroken = textUtf8.includes('�')

        if (looksBroken) {
            reader.readAsText(file, 'windows-1252')
        } else {
            parseFile(textUtf8)
        }
    }

    reader.readAsText(file, 'utf-8')
}

const addNewRow = () => {

    // TODO: fix length. This is a temp fix to avoid issues when adding rows without uploading a file first
    const length = data.value.csvData[0]?.length || (props.isSubAccount ? 12 : 7)

    const newRow: CsvDataRow = [uuidv4(), ...Array(length).fill('')]
    data.value.csvData = [...data.value.csvData, newRow]

    gridApi.value!.applyTransaction({
        add: [newRow]
    })

    const rowCount = gridApi.value!.getDisplayedRowCount()
    const lastRowIndex = rowCount - 1

    gridApi.value!.ensureIndexVisible(lastRowIndex, 'bottom')

    const firstColKey = data.value.columnConfig[0]?.field
    if (!firstColKey) {
        return
    }

    gridApi.value!.startEditingCell({
        rowIndex: lastRowIndex,
        colKey: firstColKey
    })
}

const reassignSubAccountUuids = () => {
    if (props.isSubAccount) {
        if(props.isCustomer){
            useCustomerBalance().reAssignSubAccountUuids()
        } else {
            useSupplierBalance().reAssignSubAccountUuids()
        }
    }
}

const reAssignSubAccountUuidForRow = (row: CsvDataRow) => {
    if(props.isCustomer) {
        useCustomerBalance().reAssignSubAccountUuidForRow(row)
    } else {
        useSupplierBalance().reAssignSubAccountUuidForRow(row)
    }
}


</script>

<style scoped>

</style>